<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import AttributeBadge from 'dashboard/components-next/CustomAttributes/AttributeBadge.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const getters = useStoreGetters();
const store = useStore();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const [showAddPopup, toggleAddPopup] = useToggle(false);
const selectedTabIndex = ref(0);
const searchQuery = ref('');
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const selectedAttribute = ref({});
const attributeModels = ['conversation_attribute', 'contact_attribute'];

// One drawer at a time: the one that is open lets go first (and asks when it has changes).
const addDrawerRef = ref(null);
const editDrawerRef = ref(null);
const guarded = action => {
  const openDrawer = addDrawerRef.value || editDrawerRef.value;
  return openDrawer ? openDrawer.runGuarded(action) : action();
};
const openAddPopup = () =>
  guarded(() => {
    toggleEditPopup(false);
    toggleAddPopup(true);
  });
const hideAddPopup = () => {
  toggleAddPopup(false);
};
const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedAttribute.value = {};
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
};

const tabs = computed(() => {
  return [
    {
      key: 0,
      name: t('ATTRIBUTES_MGMT.TABS.CONVERSATION'),
    },
    {
      key: 1,
      name: t('ATTRIBUTES_MGMT.TABS.CONTACT'),
    },
  ];
});

const tableHeaders = computed(() => [
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.NAME'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.TYPE'),
  t('ATTRIBUTES_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('SETTINGS_TABLE.ACTIONS'),
]);

onMounted(() => {
  store.dispatch('attributes/get');
});

const attributeModel = computed(
  () => attributeModels[selectedTabIndex.value] || 'conversation_attribute'
);

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(attributeModel.value)
);

const onClickTabChange = tab => {
  selectedTabIndex.value = tab.key;
  searchQuery.value = '';
};

const handleEditAttribute = attribute =>
  guarded(() => {
    toggleAddPopup(false);
    selectedAttribute.value = attribute;
    toggleEditPopup(true);
  });

const handleDeleteAttribute = attribute => {
  selectedAttribute.value = attribute;
  toggleDeletePopup(true);
};

const onRowAction = (action, attribute) => {
  if (action === 'edit') handleEditAttribute(attribute);
  else handleDeleteAttribute(attribute);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const derivedAttributes = computed(() =>
  attributes.value.map(attribute => ({
    ...attribute,
    label: attribute.attribute_display_name,
    type: attribute.attribute_display_type,
    value: attribute.attribute_key,
    badges: buildBadges(attribute),
  }))
);

const filteredAttributes = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return derivedAttributes.value;
  return picoSearch(derivedAttributes.value, query, [
    'attribute_display_name',
    'attribute_key',
    'attribute_description',
  ]);
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
    :no-records-found="!attributes.length"
    :no-records-message="$t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('ATTRIBUTES_MGMT.HEADER')"
        :description="$t('ATTRIBUTES_MGMT.DESCRIPTION')"
        :link-text="$t('ATTRIBUTES_MGMT.LEARN_MORE')"
        :search-placeholder="$t('ATTRIBUTES_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="custom_attributes"
      >
        <template v-if="attributes?.length" #count>
          {{ $t('ATTRIBUTES_MGMT.COUNT', { n: attributes.length }) }}
        </template>
        <template #tabs>
          <Tabs
            :model-value="String(selectedTabIndex)"
            @update:model-value="key => onClickTabChange({ key: Number(key) })"
          >
            <TabsList variant="line" :aria-label="$t('ATTRIBUTES_MGMT.HEADER')">
              <TabsTrigger
                v-for="tab in tabs"
                :key="tab.key"
                :value="String(tab.key)"
              >
                {{ tab.name }}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </template>
        <template #actions>
          <Button
            :label="$t('ATTRIBUTES_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #drawer>
      <AddAttribute
        v-if="showAddPopup"
        ref="addDrawerRef"
        :on-close="hideAddPopup"
        :selected-attribute-model-tab="selectedTabIndex"
      />
      <EditAttribute
        v-if="showEditPopup"
        :key="selectedAttribute.id"
        ref="editDrawerRef"
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="hideEditPopup"
      />
    </template>
    <template #emptyActions>
      <Button
        :label="$t('ATTRIBUTES_MGMT.HEADER_BTN_TXT')"
        size="sm"
        outline
        @click="openAddPopup"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredAttributes"
        :no-data-message="$t('ATTRIBUTES_MGMT.NO_RESULTS')"
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="attribute in items"
            :key="attribute.id"
            :item="attribute"
          >
            <BaseTableCell>
              <span class="flex min-w-0 items-center gap-2">
                <span class="truncate">{{ attribute.label }}</span>
                <AttributeBadge
                  v-for="badge in attribute.badges"
                  :key="badge.type"
                  :type="badge.type"
                />
              </span>
              <span
                class="block truncate font-mono type-polaris-caption font-normal text-polaris-muted-foreground"
              >
                {{ attribute.value }}
              </span>
            </BaseTableCell>
            <BaseTableCell>
              <Label :label="attribute.type" compact />
            </BaseTableCell>
            <BaseTableCell wrap>
              {{ attribute.attribute_description }}
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                :label="rowMenuLabel(attribute.label)"
                :items="rowMenuItems()"
                @action="action => onRowAction(action, attribute)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      :message="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="`${$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.YES')} ${
        selectedAttribute.attribute_display_name || ''
      }`"
      :reject-text="$t('ATTRIBUTES_MGMT.DELETE.CONFIRM.NO')"
      :confirm-value="selectedAttribute.attribute_display_name"
      :confirm-place-holder-text="
        $t('ATTRIBUTES_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
          attributeName: selectedAttribute.attribute_display_name,
        })
      "
      @on-confirm="confirmDeleteAttribute"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
